import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { BusyButton } from '@ztechapps/webui.button'
import map from 'lodash/map'
import find from 'lodash/find'

import { TaskStatus as TaskStatuses, type TaskStatusEnum } from '@ztechapps/common.contract'
import { useTaskStateTransition } from '@ztechapps/task.common'
import { findStatus, HorizontalStack, resolveStatusColor } from '@ztechapps/webui.util'
import { TaskStatus } from './TaskStatusBadge'
import { useError } from '@ztechapps/common.hook'

export type TaskStatusDropdownProps = {
  isBusy?: boolean
  onChange?: (status: string) => Promise<void>
  disabled?: boolean
  value: string
  taskId: string
}
export const TaskStatusDropdown = ({ onChange, disabled = false, value, taskId }: TaskStatusDropdownProps) => {
  const { onError } = useError()
  const selectedStatus = findStatus(TaskStatuses, value)
  const selectedStatusColor = resolveStatusColor(selectedStatus)
  const selectedStatusStyle = {
    backgroundColor: selectedStatusColor?.fill,
    borderColor: selectedStatusColor?.fill,
    color: selectedStatusColor?.text,
  }
  const [isBusy, setIsBusy] = React.useState(false)

  // getTaskStatusCounts refetch is a no-op when no component watches that query (e.g., task detail page)
  const [{ changeStatus }] = useTaskStateTransition({
    options: {
      mutationOptions: {
        refetchQueries: ['getTaskStatusCounts'],
        awaitRefetchQueries: true,
      },
    },
  })

  const onUpdate = async (newStatus: string) => {
    try {
      setIsBusy(true)

      await changeStatus(newStatus as TaskStatusEnum, taskId)

      await onChange?.(newStatus)
    } catch (error) {
      onError(error)
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <HorizontalStack gap={0}>
      <div>
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-light"
            size="sm"
            style={selectedStatusStyle}
            id="dropdown-basic"
            disabled={disabled || isBusy}
          >
            {selectedStatus?.value}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {map(TaskStatuses, (item, key) => {
              return (
                <Dropdown.Item disabled={isBusy} key={key} eventKey={item?.value} onClick={() => onUpdate(item.value)}>
                  <HorizontalStack gap={1}>
                    <div>
                      <TaskStatus status={item?.value} />
                    </div>
                    <div>{item?.value}</div>
                  </HorizontalStack>
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div>
        <BusyButton
          bsSize="sm"
          bsStyle="outline-light"
          disabled={disabled}
          icon={'play'}
          isBusy={isBusy}
          busyText={' '}
          title={'Click this to update to next status.'}
          onClick={() => {
            let newStatus = find(TaskStatuses, (item) => {
              return item?.sort === selectedStatus?.sort + 1
            })
            if (!newStatus)
              newStatus = find(TaskStatuses, (item) => {
                return item?.sort === 0
              })

            return onUpdate(newStatus.value)
          }}
          style={selectedStatusStyle}
        />
      </div>
    </HorizontalStack>
  )
}
